"use client"
import React, { useState } from "react";
import { apiFetch } from "@/utils/api";
import { LoaderIcon, EyeIcon, EyeOffIcon } from "@/utils/icons";
import { useRouter } from "next/navigation";

export default function RegisterPage() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const router = useRouter();

  const validateEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (!username || username.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await apiFetch("/auth/register", {
        method: "POST",
        body: JSON.stringify({ username, email, password }),
      });
      setSuccess(true);
      setError("");
      setUsername("");
      setEmail("");
      setPassword("");
      setTimeout(()=> router.push("/login"),1200)
      // Optionally redirect or update global auth state here
    } catch (err: unknown) {
      setError((err as Error).message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-16 px-4">
      <h1 className="text-2xl font-bold mb-6">Register</h1>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 bg-white rounded-lg shadow p-6" aria-label="Register form">
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-base bg-white transition"
          required
          aria-label="Username"
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-base bg-white transition"
          required
          aria-label="Email address"
        />
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-base bg-white transition w-full pr-10"
            required
            aria-label="Password"
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
          </button>
        </div>
        <button
          type="submit"
          className="bg-primary-500 text-white px-6 py-2 rounded font-semibold hover:bg-primary-600 transition flex items-center justify-center gap-2 disabled:opacity-60"
          disabled={loading}
        >
          {loading && <LoaderIcon className="animate-spin h-5 w-5" />} REGISTER
        </button>
        {error && <div className="text-red-500 text-sm mt-2" role="alert">{error}</div>}
        {success && <div className="text-green-600 text-sm mt-2" role="status">Registration successful! You can now log in.</div>}
        <div className="text-sm text-gray-500 mt-2">
          Already have an account? <a href="/login" className="text-primary-600 hover:underline">Login</a>
        </div>
      </form>
    </div>
  );
} 